import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AuthApi, UnauthApi } from '@/api';
import { useStatusBarColor } from '@/hooks/useStatusBarColor';
import { useAuthStore } from '@/stores/AuthStore';
import type { DeviceTokenData } from '@/stores/DeviceStore';
import { STATUS_COLOR, useDeviceInfoStore
// WEBVIEW_TYPE,
} from '@/stores/DeviceStore';
import { MESSAGE_KEYWORD } from '../_utils/reserved-keywords';
import { useHandleApp } from './useHandleApp';

//* 3.1.20 앱 스토어 미배포로 강제 업데이트 임시 비활성 — 배포 후 이 파일의 주석 전부 해제 (isAppVersionAtLeast import 포함)
// 애드팝콘 SSP 광고 SDK 최소 요구 버전 — 미만이면 강제 업데이트
// const MIN_APP_VERSION = '3.1.20';

export const useMountAppData = () => {
  const $ = _c(41);
  const [osType, setAppVersion, tempDeviceTokenData, setTempDeviceTokenData, deviceTokenData, setDeviceTokenData] = useDeviceInfoStore(useShallow(_temp));
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: MESSAGE_KEYWORD.VERSION
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    request: requestAppVersion,
    response: responseAppVersion
  } = useHandleApp(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: MESSAGE_KEYWORD.NOTIFICATION
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    request: requestNotification,
    response: responseNotification
  } = useHandleApp(t1);
  let t2;
  let t3;
  if ($[2] !== osType || $[3] !== requestAppVersion) {
    t2 = () => {
      if (!osType) {
        return;
      }
      requestAppVersion();
    };
    t3 = [requestAppVersion, osType];
    $[2] = osType;
    $[3] = requestAppVersion;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const isFirstLoading = responseAppVersion?.isSuccess && responseAppVersion.data ? responseAppVersion.data.isFirstLoading : "Y";
  let t4;
  let t5;
  if ($[6] !== responseAppVersion || $[7] !== setAppVersion) {
    t4 = () => {
      if (responseAppVersion?.isSuccess && responseAppVersion?.data) {
        const {
          version
        } = responseAppVersion.data;
        setAppVersion(version);
      }
    };
    t5 = [responseAppVersion, setAppVersion];
    $[6] = responseAppVersion;
    $[7] = setAppVersion;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== deviceTokenData || $[11] !== isFirstLoading || $[12] !== osType || $[13] !== requestNotification) {
    t6 = () => {
      if (!osType) {
        return;
      }
      if (!deviceTokenData && isFirstLoading === "N") {
        requestNotification();
      }
    };
    t7 = [deviceTokenData, requestNotification, isFirstLoading, osType];
    $[10] = deviceTokenData;
    $[11] = isFirstLoading;
    $[12] = osType;
    $[13] = requestNotification;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  const isLogin = useAuthStore(_temp2);
  const {
    toastError
  } = useToast();
  let t8;
  if ($[16] !== isLogin) {
    t8 = requestBody => isLogin ? AuthApi["post:/wello/v2/auth/wello-device-info"]({
      body: {
        ...requestBody
      }
    }) : UnauthApi["post:/wello/v2/allows/wello-device-info"]({
      body: {
        ...requestBody
      }
    });
    $[16] = isLogin;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== setDeviceTokenData || $[19] !== tempDeviceTokenData) {
    t9 = () => {
      if (tempDeviceTokenData) {
        setDeviceTokenData(tempDeviceTokenData);
      }
    };
    $[18] = setDeviceTokenData;
    $[19] = tempDeviceTokenData;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== toastError) {
    t10 = error => toastError({
      error
    });
    $[21] = toastError;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8 || $[25] !== t9) {
    t11 = {
      mutationFn: t8,
      onSuccess: t9,
      onError: t10
    };
    $[23] = t10;
    $[24] = t8;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const {
    mutate: saveDeviceTokenMutate
  } = useMutation(t11);
  let t12;
  if ($[27] !== isLogin || $[28] !== responseNotification || $[29] !== setTempDeviceTokenData) {
    t12 = () => {
      if (!responseNotification || isLogin === undefined) {
        return;
      }
      const {
        isSuccess,
        data
      } = responseNotification;
      if (!isSuccess || !data) {
        return;
      }
      setTempDeviceTokenData(data);
    };
    $[27] = isLogin;
    $[28] = responseNotification;
    $[29] = setTempDeviceTokenData;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== isLogin || $[32] !== responseNotification || $[33] !== saveDeviceTokenMutate || $[34] !== setTempDeviceTokenData) {
    t13 = [isLogin, responseNotification, saveDeviceTokenMutate, setTempDeviceTokenData];
    $[31] = isLogin;
    $[32] = responseNotification;
    $[33] = saveDeviceTokenMutate;
    $[34] = setTempDeviceTokenData;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  useEffect(t12, t13);
  let t14;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      color: STATUS_COLOR.DEFAULT
    };
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  useStatusBarColor(t14);
  let t15;
  let t16;
  if ($[37] !== saveDeviceTokenMutate || $[38] !== tempDeviceTokenData) {
    t15 = () => {
      if (tempDeviceTokenData) {
        saveDeviceTokenMutate(tempDeviceTokenData);
      }
    };
    t16 = [saveDeviceTokenMutate, tempDeviceTokenData];
    $[37] = saveDeviceTokenMutate;
    $[38] = tempDeviceTokenData;
    $[39] = t15;
    $[40] = t16;
  } else {
    t15 = $[39];
    t16 = $[40];
  }
  useEffect(t15, t16);
};
function _temp(state) {
  return [state.osType, state.setAppVersion, state.tempDeviceTokenData, state.setTempDeviceTokenData, state.deviceTokenData, state.setDeviceTokenData];
}
function _temp2(state_0) {
  return state_0.isLogin;
}